if (!customElements.get('article-toc')) {
  class ArticleToc extends HTMLElement {
    constructor() {
      super();
      this.target = null;
      this.headings = [];
      this.linksByHash = new Map();
      this.activeLink = null;
    }

    connectedCallback() {
      const targetSelector = this.dataset.target || '.article__content';
      this.target = document.querySelector(targetSelector);

      if (!this.target) {
        return;
      }

      const threshold = parseInt(this.dataset.threshold, 10) || 4;
      this.headings = Array.from(this.target.querySelectorAll('h2, h3'));

      // Only count H2s against the threshold; H3s are sub-items
      const h2Count = this.headings.filter((h) => h.tagName === 'H2').length;

      if (h2Count < threshold) {
        // Stay hidden
        return;
      }

      this.buildList();
      this.removeAttribute('hidden');
      this.bindEvents();
      this.handleScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.scrollHandler);
    }

    /**
     * Generate a slug-style id from a heading element if it doesn't have one
     */
    ensureHeadingId(heading) {
      if (heading.id) {
        return heading.id;
      }
      const baseSlug = (heading.textContent || '')
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .substring(0, 64) || 'section';

      let candidate = baseSlug;
      let suffix = 1;
      while (document.getElementById(candidate)) {
        suffix += 1;
        candidate = baseSlug + '-' + suffix;
      }
      heading.id = candidate;
      return candidate;
    }

    buildList() {
      const list = this.querySelector('.js-article-toc-list');
      if (!list) return;

      const fragment = document.createDocumentFragment();

      this.headings.forEach((heading) => {
        const id = this.ensureHeadingId(heading);
        const item = document.createElement('li');
        item.className = 'article-toc__item article-toc__item--' + heading.tagName.toLowerCase();

        const link = document.createElement('a');
        link.className = 'article-toc__link';
        link.href = '#' + id;
        link.textContent = heading.textContent.trim();

        link.addEventListener('click', this.handleLinkClick.bind(this, id));

        item.appendChild(link);
        fragment.appendChild(item);

        this.linksByHash.set(id, link);
      });

      list.appendChild(fragment);
    }

    bindEvents() {
      this.scrollHandler = this.handleScroll.bind(this);
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }

    handleLinkClick(id, evt) {
      // Allow native anchor behaviour but offset for sticky header
      const target = document.getElementById(id);
      if (!target) return;
      evt.preventDefault();
      const offset = 80;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: top, behavior: 'smooth' });
      history.pushState(null, '', '#' + id);
    }

    handleScroll() {
      // Update scroll progress bar
      const rect = this.target.getBoundingClientRect();
      const total = rect.height + this.target.offsetTop - window.innerHeight + 200;
      const percent = Math.max(0, Math.min(100, (window.scrollY / total) * 100));
      this.style.setProperty('--toc-progress', percent + '%');

      // Highlight active heading (scroll-spy)
      const triggerLine = window.innerHeight * 0.3;
      let current = null;
      for (let i = 0; i < this.headings.length; i += 1) {
        const heading = this.headings[i];
        const top = heading.getBoundingClientRect().top;
        if (top <= triggerLine) {
          current = heading.id;
        } else {
          break;
        }
      }

      if (current && this.linksByHash.has(current)) {
        const link = this.linksByHash.get(current);
        if (link !== this.activeLink) {
          if (this.activeLink) {
            this.activeLink.classList.remove('is-active');
          }
          link.classList.add('is-active');
          this.activeLink = link;
        }
      }
    }
  }

  customElements.define('article-toc', ArticleToc);
}
